import { ISelectedItem } from '../../../components/Inputs/Selects/MultiSelectCheck';
import { Dimensions, ExtendedRight } from '../../../interfaces/Rights';

// Árbol del filtro: derecho > tipo de indicador > dimensión > subdimensión > indicador.
// Dimensiones, subdimensiones e indicadores con el mismo nombre se unifican en una sola opción:
// en el filtro se identifican por nombre y al back / URL se mandan todos los ids que coinciden.

type TreeItem = { id: number; titulo: string };

export interface FilterSelection {
    types: string[];
    dimensions: ISelectedItem[];
    subdimensions: ISelectedItem[];
    indicators: ISelectedItem[];
}

const normalize = (value: string) => value.toLowerCase().trim();

// una opción por nombre (las repetidas se unifican)
export const uniqueByTitle = <T extends TreeItem>(items: T[]): T[] =>
    [...new Map(items.map(item => [normalize(item.titulo), item])).values()];

// todos los elementos cuyo nombre está seleccionado (incluye los repetidos de otros derechos/tipos)
export const matchByTitle = <T extends TreeItem>(items: T[], selected: ISelectedItem[]): T[] => {
    const titles = selected.map(s => normalize(s.value));
    return items.filter(item => titles.includes(normalize(item.titulo)));
};

// ids de todo lo que coincide por nombre: es lo que va al back y a la URL.
// Sin repetidos: un mismo elemento (mismo id) puede colgar de varias ramas del árbol
export const getIdsByTitle = (items: TreeItem[], selected: ISelectedItem[]): string[] =>
    [...new Set(matchByTitle(items, selected).map(item => String(item.id)))];

// ids de la URL -> seleccionados (uno por nombre)
export const selectedFromIds = (items: TreeItem[], ids: string[]): ISelectedItem[] =>
    uniqueByTitle(items.filter(item => ids.includes(String(item.id))))
        .map(item => ({ value: item.titulo, label: item.titulo, id: item.id }));

// conserva lo seleccionado cuyo nombre sigue existiendo
export const keepExisting = (selected: ISelectedItem[], available: TreeItem[]): ISelectedItem[] => {
    const titles = available.map(item => normalize(item.titulo));
    return selected.filter(s => titles.includes(normalize(s.value)));
};

// dimensiones de los tipos elegidos en los derechos elegidos (con repetidas)
export const getDimensionsOf = (rights: ExtendedRight[], types: string[]): Dimensions[] => {
    const normalizedTypes = types.map(normalize);
    return rights
        .flatMap(r => r.tipos_indicadores ?? [])
        .filter(t => normalizedTypes.includes(normalize(t.titulo)))
        .flatMap(t => t.dimensiones);
};

// ids (URL) -> seleccionados de cada nivel, recorriendo el árbol de los derechos/tipos dados
export const selectionFromIds = (
    rights: ExtendedRight[],
    types: string[],
    ids: { dimensions: string[]; subdimensions: string[]; indicators: string[] }
): Omit<FilterSelection, "types"> => {
    const dimensions = getDimensionsOf(rights, types);
    const subdimensions = dimensions.flatMap(d => d.subdimensiones);
    const indicators = subdimensions.flatMap(s => s.indicadores);

    return {
        dimensions: selectedFromIds(dimensions, ids.dimensions),
        subdimensions: selectedFromIds(subdimensions, ids.subdimensions),
        indicators: selectedFromIds(indicators, ids.indicators),
    };
};

// cascada completa: todo lo disponible en cada nivel + lo seleccionado que sigue existiendo
export const resolveFilterTree = (rights: ExtendedRight[], selection: FilterSelection) => {
    const dimensions = getDimensionsOf(rights, selection.types);
    const keptDimensions = keepExisting(selection.dimensions, dimensions);

    const subdimensions = matchByTitle(dimensions, keptDimensions).flatMap(d => d.subdimensiones);
    const keptSubdimensions = keepExisting(selection.subdimensions, subdimensions);

    const indicators = matchByTitle(subdimensions, keptSubdimensions).flatMap(s => s.indicadores);
    const keptIndicators = keepExisting(selection.indicators, indicators);

    return {
        all: { dimensions, subdimensions, indicators },
        kept: { dimensions: keptDimensions, subdimensions: keptSubdimensions, indicators: keptIndicators },
    };
};

export const rightTitlesToIds = (rights: ExtendedRight[], titles: string[]) =>
    rights.filter(r => titles.includes(r.titulo)).map(r => String(r.id));

// breadcrumbs del buscador ("Derecho > Tipo > Dimensión > Subdimensión") -> ids del árbol.
// Se busca en todos los derechos: el indicador puede ser de uno que no está seleccionado
export const findIndicatorPath = (rights: ExtendedRight[], breadcrumbs: string) => {
    const [rightName, type, dimension, subdimension] = breadcrumbs
        .replace(/>/g, "/")
        .split("/")
        .map(p => p.trim())
        .filter(Boolean);

    const sameTitle = (item: TreeItem, title?: string) => normalize(item.titulo) === normalize(title ?? "");

    const right = rights.find(r => sameTitle(r, rightName));
    const foundType = right?.tipos_indicadores.find(t => sameTitle(t, type));
    const foundDimension = foundType?.dimensiones.find(d => sameTitle(d, dimension));
    const foundSubdimension = foundDimension?.subdimensiones.find(s => sameTitle(s, subdimension));

    return {
        rightId: right ? String(right.id) : undefined,
        type,
        dimensionID: foundDimension ? String(foundDimension.id) : undefined,
        subdimensionID: foundSubdimension ? String(foundSubdimension.id) : undefined,
    };
};
